//* 로그인 실패 안내 — 폼 아래 인라인 (토스트 대신). 백엔드 스펙: smartbugo-api/docs/login-device-lock.md
import { FC, useEffect, useState } from 'react';

import { LoginFailure, lockMinutesOf } from '@shared/utils/loginError.util';
import { formatMmSs, pad2 } from '@shared/utils/timeFormat.util';

/**
 * 몇 번 남았는지가 화면에 남아 있어야 전화로 이어진다 — 1.5초 토스트로는 안 된다.
 *
 * - 불일치: 빨간 한 줄 + (서버가 횟수를 줬을 때만) "틀린횟수 (3/5) · 5회 시 60분 잠김"
 * - 잠김: 해제 시각 + 카운트다운 + [문의 남기기] + "해제받으셨다면 [다시 시도]"
 * - 번호 미등록: 본인이 할 수 있는 일이 없다 → [문의 남기기] 를 크게
 *
 * 전화번호는 두지 않는다 — 문의는 Slack 으로 바로 가고, 번호를 아는 사람은 어차피 전화한다.
 *
 * 횟수는 서버 값만 쓴다. 고객(잠금 대상 아님)과 직원이 같은 폼을 쓰므로 프론트에서 세면
 * 고객에게 없는 잠금을 경고하게 된다.
 */

/** 남은 횟수가 이 값 이하면 빨갛게 강조 */
const WARN_ATTEMPTS_LEFT = 2;

const clockOf = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const remainingOf = (iso: string): number =>
  Math.max(0, new Date(iso).getTime() - Date.now());
//* 남은 ms → "mm:ss" (분도 두 자리). 올림이라 59.2초는 1:00 으로 보인다
const mmss = (ms: number): string => formatMmSs(Math.ceil(ms / 1000), true);

/** Slack 으로 가는 로그인 문의 폼을 연다. 강조 단계에서는 옅은 배경까지 */
const HelpLine: FC<{ strong?: boolean; onClick: () => void }> = ({ strong, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center rounded-full font-semibold underline underline-offset-2 theme-text-main ${
      strong ? 'bg-gray-100 px-3 py-1 text-13 no-underline' : 'text-12'
    }`}
  >
    문의 남기기
  </button>
);

interface Props {
  failure: LoginFailure;
  /** 잠금의 [다시 시도] — 로컬 잠금 캐시를 지우고 폼을 다시 연다 */
  onRetry: () => void;
  /** 카운트다운이 0 이 됐을 때 */
  onLockExpired: () => void;
  /** [문의 남기기] — 로그인 문의 폼(Slack)으로 */
  onHelp: () => void;
}

export const LoginFailureCard: FC<Props> = function LoginFailureCard({
  failure,
  onRetry,
  onLockExpired,
  onHelp,
}: Props) {
  if (failure.kind === 'locked') {
    return (
      <LockedNotice
        lockedUntil={failure.lockedUntil}
        onRetry={onRetry}
        onLockExpired={onLockExpired}
        onHelp={onHelp}
      />
    );
  }

  if (failure.kind === 'phone-not-registered') {
    return (
      <div role="alert" className="w-full space-y-2 text-center">
        <p className="break-keep text-14 font-medium text-red-500">
          {failure.message ||
            '등록된 휴대폰번호가 없어 로그인할 수 없습니다. 관리자에게 문의해 주세요.'}
        </p>
        <p className="break-keep text-12 text-gray-500">
          관리자 계정은 등록된 휴대폰으로 본인 확인을 마쳐야 로그인됩니다.
        </p>
        <HelpLine strong onClick={onHelp} />
      </div>
    );
  }

  if (failure.kind === 'other') {
    return (
      <div role="alert" className="w-full break-keep text-center text-14 font-medium text-red-500">
        {failure.message || '로그인 중 문제가 생겼습니다. 잠시 후 다시 시도해 주세요.'}
      </div>
    );
  }

  const { failCount, maxFail } = failure;
  const counted = typeof failCount === 'number' && typeof maxFail === 'number';
  const attemptsLeft =
    failure.attemptsLeft ?? (counted ? Math.max(0, maxFail - failCount) : undefined);
  const warn = typeof attemptsLeft === 'number' && attemptsLeft <= WARN_ATTEMPTS_LEFT;

  return (
    <div role="alert" className="w-full space-y-1 text-center">
      <p className="break-keep text-14 font-medium text-red-500">
        아이디 또는 비밀번호가 일치하지 않습니다.
      </p>
      {counted && (
        <p className="break-keep text-12 text-gray-500">
          <span className={warn ? 'font-bold text-red-500' : 'font-semibold text-gray-700'}>
            틀린횟수 ({failCount}/{maxFail})
          </span>
          {/* 비상 스위치 off 면 failCount 가 maxFail 을 넘겨 내려온다 — 그때는 임계치 문장을 뺀다 */}
          {failCount < maxFail && (
            <>
              {' · '}
              {maxFail}회 시 {lockMinutesOf(maxFail)}분 잠김
            </>
          )}
        </p>
      )}
      {counted && <HelpLine strong={warn} onClick={onHelp} />}
    </div>
  );
};

interface LockedProps {
  lockedUntil: string;
  onRetry: () => void;
  onLockExpired: () => void;
  onHelp: () => void;
}

const LockedNotice: FC<LockedProps> = function LockedNotice({
  lockedUntil,
  onRetry,
  onLockExpired,
  onHelp,
}: LockedProps) {
  const [remaining, setRemaining] = useState(() => remainingOf(lockedUntil));

  useEffect(() => {
    setRemaining(remainingOf(lockedUntil));
    const timer = window.setInterval(() => {
      const left = remainingOf(lockedUntil);
      setRemaining(left);
      if (left <= 0) {
        window.clearInterval(timer);
        onLockExpired();
      }
    }, 1000);
    return () => window.clearInterval(timer);
    //* onLockExpired 는 부모의 setState 래퍼라 매 렌더 바뀔 수 있어 의존성에서 뺀다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedUntil]);

  const until = clockOf(lockedUntil);

  return (
    <div role="alert" className="w-full space-y-1 text-center">
      <p className="break-keep text-14 font-semibold text-red-500">
        로그인 시도가 많아 {until ? `${until}까지 ` : '잠시 '}잠겼습니다.
      </p>
      <p className="break-keep text-12 text-gray-600">
        남은 시간 <span className="font-mono font-semibold tabular-nums">{mmss(remaining)}</span>{' '}
        · 문의를 남기시면 바로 풀어드립니다.
      </p>
      <HelpLine strong onClick={onHelp} />
      <p className="break-keep text-12 text-gray-500">
        해제받으셨다면{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold underline underline-offset-2 theme-text-main"
        >
          다시 시도
        </button>
        해 주세요.
      </p>
    </div>
  );
};
